import { AppData, BookingData, BookingTool, Step } from "../../AppDataProvider";
import {
  CompleteAvailabilityStepPayload,
  RecurringIntervalSelected,
  ResourceAfterAvailability,
} from "../../actions";
import {
  ResourceAllocationType,
  ResourceSelectionMode,
} from "../../../GeneratedGraphQL";

import { Mixpanel } from "../../../mixpanel/mixpanel";
import { PartRequired } from "../../../utils/types";
import { requireBookingDataFields } from "../../../utils";

export type CompleteAvailabilityStepOptions = {
  appData: AppData;
  payload?: CompleteAvailabilityStepPayload;
};

type CompleteAvailabilityStepBookingData = PartRequired<
  BookingData,
  "organisation" | "service"
>;

const getNextStep = (
  bookingData: BookingData,
  bookingTool: BookingTool,
): Step => {
  requireBookingDataFields<CompleteAvailabilityStepBookingData>(bookingData, [
    "organisation",
    "service",
  ]);

  const { resourceSelectionMode } = bookingTool;
  const { organisation, recurringBooking, selectedTimeSlot, service } =
    bookingData;
  const {
    allowBookingNotes,
    externalBookingQuestions,
    resourceSelectionBeforeAvailability,
  } = organisation;

  if (recurringBooking) return "recurringBooking";

  /**
   * If we have been provided a resourceId in the url params,
   * we want to navigate to the resources step where the
   * provided resource will be automatically selected
   */
  const urlParams = new URLSearchParams(window.location.search);
  const preFillResourceId = urlParams.get("resourceId");

  if (
    selectedTimeSlot &&
    !resourceSelectionBeforeAvailability &&
    service.resourceAllocationType === ResourceAllocationType.Any &&
    (resourceSelectionMode !== ResourceSelectionMode.Disabled ||
      preFillResourceId)
  ) {
    return "resources";
  }

  const hasRelevantBookingQuestions = externalBookingQuestions.some(
    (question) =>
      !question.services ||
      question.services.map(({ _id }) => _id).includes(service._id),
  );

  if (allowBookingNotes || hasRelevantBookingQuestions) {
    return "bookingQuestions";
  }

  return "details";
};

function isRecurringIntervalSelected(
  updatedData: CompleteAvailabilityStepPayload["updatedData"],
): updatedData is RecurringIntervalSelected {
  return (
    (updatedData as RecurringIntervalSelected).recurringBooking !== undefined
  );
}

function isResourceSelectionAfterAvailability(
  updatedData: CompleteAvailabilityStepPayload["updatedData"],
): updatedData is ResourceAfterAvailability {
  return (
    (updatedData as ResourceAfterAvailability).selectedTimeSlot !== undefined
  );
}

export const completeAvailabilityStep = (
  options: CompleteAvailabilityStepOptions,
): AppData => {
  Mixpanel.track({ name: "Completed step", props: { step: "availability" } });

  const { bookingData, bookingTool } = options.appData;

  if (!options.payload) {
    return {
      ...options.appData,
      currentStep: getNextStep(bookingData, bookingTool),
    };
  }

  const { updatedData } = options.payload;

  let updatedBookingData = {};

  if (isRecurringIntervalSelected(updatedData)) {
    const { attendees, duration, recurringBooking, timezone } = updatedData;

    updatedBookingData = {
      ...bookingData,
      attendees,
      duration,
      recurringBooking,
      timezone,
    };
  } else if (isResourceSelectionAfterAvailability(updatedData)) {
    const { attendees, duration, timezone, selectedTimeSlot } = updatedData;

    updatedBookingData = {
      ...bookingData,
      attendees,
      duration,
      selectedTimeSlot,
      timezone,
      ...("resourceIds" in selectedTimeSlot.interval && {
        resourceIds: selectedTimeSlot.interval.resourceIds,
      }),
    };
  } else {
    const {
      attendees,
      duration,
      reservedSpaceId,
      startDate,
      timezone,
      selectedBookings,
      resourceIds,
    } = updatedData;

    updatedBookingData = {
      ...bookingData,
      attendees,
      duration,
      reservedSpaceId,
      resourceIds,
      selectedBookings,
      startDate,
      timezone,
    };
  }

  const nextStep = getNextStep(updatedBookingData, bookingTool);

  return {
    ...options.appData,
    bookingData: updatedBookingData,
    currentStep: nextStep,
  };
};
